import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import Snackbar from "@material-ui/core/Snackbar";
import TextField from "@material-ui/core/TextField";
import Divider from "@material-ui/core/Divider";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

const STATUS_META = {
  reputation_applied: {
    label: "Reputation Applied",
    backgroundColor: "rgba(255, 152, 0, 0.18)",
    color: "#ffb74d",
  },
  appealed: {
    label: "Appealed",
    backgroundColor: "rgba(156, 39, 176, 0.18)",
    color: "#ce93d8",
  },
  reversed: {
    label: "Reversed",
    backgroundColor: "rgba(76, 175, 80, 0.18)",
    color: "#81c784",
  },
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    minHeight: "100vh",
    backgroundColor: theme.palette.background.default,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  headerText: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  statusChip: {
    fontWeight: 600,
    letterSpacing: "0.02em",
  },
  list: {
    display: "grid",
    gap: theme.spacing(2),
  },
  card: {
    padding: theme.spacing(3),
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  highlightedCard: {
    borderColor: theme.palette.primary.main,
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  cardTitle: {
    fontSize: "1.1rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  mutedText: {
    color: theme.palette.text.secondary,
  },
  metadataGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  metadataLabel: {
    fontSize: "0.78rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  metadataValue: {
    color: theme.palette.text.primary,
    wordBreak: "break-word",
  },
  reversalSection: {
    marginTop: theme.spacing(2),
    display: "grid",
    gap: theme.spacing(1.5),
  },
  emptyState: {
    padding: theme.spacing(4),
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  actionRow: {
    display: "flex",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
    alignItems: "center",
  },
}));

function formatTimestamp(value) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDelta(delta) {
  const normalizedDelta = Number(delta || 0);
  if (!Number.isFinite(normalizedDelta) || normalizedDelta === 0) {
    return "0";
  }

  return normalizedDelta > 0 ? `+${normalizedDelta}` : String(normalizedDelta);
}

async function readJson(response) {
  try {
    return await response.json();
  } catch (_error) {
    return {};
  }
}

export default function ReputationOutcomesPage({ catalog }) {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { steamId } = useParams();
  const user = catalog?.user;
  const [outcomes, setOutcomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submittingHistoryId, setSubmittingHistoryId] = useState("");
  const [reversalReasons, setReversalReasons] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const highlightedHistoryId = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return String(params.get("historyId") || "").trim();
  }, [location.search]);

  const loadOutcomes = useCallback(async () => {
    if (!steamId || !user?.userId) {
      return;
    }

    setLoading(true);
    setErrorMessage("");
    try {
      const response = await fetch(
        `${basePath}/api/v7/admin/reputation-outcomes/${steamId}/adpass/${user.userId}`,
        {
          credentials: "include",
        }
      );
      const data = await readJson(response);
      if (!response.ok || !data.ok) {
        throw new Error(data.error || data.err || "Failed to load reputation outcomes");
      }

      const nextOutcomes = Array.isArray(data.outcomes) ? data.outcomes : [];
      setOutcomes(nextOutcomes);
      setReversalReasons((currentReasons) => {
        const nextReasons = {};
        nextOutcomes.forEach((outcome) => {
          const historyId = String(outcome?.historyId || "").trim();
          if (!historyId) {
            return;
          }
          nextReasons[historyId] = currentReasons[historyId] || "";
        });
        return nextReasons;
      });
    } catch (error) {
      setErrorMessage(error.message || "Failed to load reputation outcomes");
      setOutcomes([]);
    } finally {
      setLoading(false);
    }
  }, [steamId, user?.userId]);

  useEffect(() => {
    loadOutcomes();
  }, [loadOutcomes]);

  const handleBack = () => {
    if (typeof history.length === "number" && history.length > 1) {
      history.goBack();
      return;
    }
    history.push("/users");
  };

  const openMatchContext = (matchUrl) => {
    const normalizedMatchUrl = String(matchUrl || "").trim();
    if (!normalizedMatchUrl || typeof window === "undefined") {
      return;
    }

    window.open(normalizedMatchUrl, "_blank", "noopener,noreferrer");
  };

  const handleReverse = async (outcome) => {
    const historyId = String(outcome?.historyId || "").trim();
    const reversalReason = String(reversalReasons[historyId] || "").trim();
    if (!historyId || !reversalReason || !user?.userId) {
      return;
    }

    setSubmittingHistoryId(historyId);
    setErrorMessage("");
    try {
      const response = await fetch(
        `${basePath}/api/v7/admin/reputation-outcomes/${historyId}/reverse/adpass/${user.userId}`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            reversalReason,
            relatedTicketId: outcome?.relatedTicketId || null,
          }),
        }
      );
      const data = await readJson(response);
      if (!response.ok || !data.ok) {
        throw new Error(data.error || data.err || "Failed to reverse reputation outcome");
      }

      setSnackbarMessage(
        data?.rebuild?.ok === false
          ? "Outcome reversed. Player rebuild needs a retry."
          : "Outcome reversed and player reputation rebuilt."
      );
      setReversalReasons((currentReasons) => ({
        ...currentReasons,
        [historyId]: "",
      }));
      await loadOutcomes();
    } catch (error) {
      setErrorMessage(error.message || "Failed to reverse reputation outcome");
    } finally {
      setSubmittingHistoryId("");
    }
  };

  if (loading) {
    return (
      <div className={classes.root}>
        <Box display="flex" justifyContent="center" py={10}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <IconButton onClick={handleBack} aria-label="Back to previous page">
          <ArrowBackIcon />
        </IconButton>
        <div className={classes.headerText}>
          <Typography className={classes.title}>
            Leaver Reputation Outcomes
          </Typography>
          <Typography className={classes.subtitle}>
            Review and reverse match leaver penalties for {steamId}.
          </Typography>
        </div>
      </div>

      {errorMessage ? (
        <Paper className={classes.emptyState}>
          <Typography color="error">{errorMessage}</Typography>
        </Paper>
      ) : null}

      {!errorMessage && outcomes.length === 0 ? (
        <Paper className={classes.emptyState}>
          <Typography variant="h6" gutterBottom>
            No leaver outcomes found
          </Typography>
          <Typography className={classes.mutedText}>
            This player has no recorded match leaver penalties yet.
          </Typography>
        </Paper>
      ) : null}

      {outcomes.length > 0 ? (
        <div className={classes.list}>
          {outcomes.map((outcome) => {
            const historyId = String(outcome?.historyId || "").trim();
            const isSubmitting = submittingHistoryId === historyId;
            const reversalReason = reversalReasons[historyId] || "";
            const statusMeta =
              STATUS_META[outcome?.status] || STATUS_META.reputation_applied;
            const dimensionsAffected = Array.isArray(outcome?.dimensionsAffected)
              ? outcome.dimensionsAffected.join(", ")
              : "-";

            return (
              <Paper
                key={historyId}
                className={`${classes.card} ${
                  highlightedHistoryId && highlightedHistoryId === historyId
                    ? classes.highlightedCard
                    : ""
                }`}
              >
                <div className={classes.cardHeader}>
                  <div>
                    <Typography className={classes.cardTitle}>
                      Match {outcome?.relatedMatchId || "Unknown"}
                    </Typography>
                    <Typography className={classes.mutedText}>
                      History ID: {historyId || "-"}
                    </Typography>
                  </div>
                  <Chip
                    className={classes.statusChip}
                    label={statusMeta.label}
                    style={{
                      backgroundColor: statusMeta.backgroundColor,
                      color: statusMeta.color,
                    }}
                  />
                </div>

                <div className={classes.metadataGrid}>
                  <div>
                    <Typography className={classes.metadataLabel}>Applied</Typography>
                    <Typography className={classes.metadataValue}>
                      {formatTimestamp(outcome?.appliedAt)}
                    </Typography>
                  </div>
                  <div>
                    <Typography className={classes.metadataLabel}>Reversed</Typography>
                    <Typography className={classes.metadataValue}>
                      {formatTimestamp(outcome?.reversedAt)}
                    </Typography>
                  </div>
                  <div>
                    <Typography className={classes.metadataLabel}>Delta</Typography>
                    <Typography className={classes.metadataValue}>
                      {formatDelta(outcome?.delta)}
                    </Typography>
                  </div>
                  <div>
                    <Typography className={classes.metadataLabel}>
                      Dimensions
                    </Typography>
                    <Typography className={classes.metadataValue}>
                      {dimensionsAffected || "-"}
                    </Typography>
                  </div>
                  <div>
                    <Typography className={classes.metadataLabel}>
                      Support Ticket
                    </Typography>
                    <Typography className={classes.metadataValue}>
                      {outcome?.relatedTicketNumber || outcome?.relatedTicketId || "-"}
                    </Typography>
                  </div>
                  <div>
                    <Typography className={classes.metadataLabel}>
                      Ticket Status
                    </Typography>
                    <Typography className={classes.metadataValue}>
                      {outcome?.relatedTicketStatus || "-"}
                    </Typography>
                  </div>
                </div>

                <div className={classes.actionRow}>
                  {outcome?.matchUrl ? (
                    <Button
                      variant="outlined"
                      size="small"
                      endIcon={<OpenInNewIcon />}
                      onClick={() => openMatchContext(outcome.matchUrl)}
                    >
                      Open Match Context
                    </Button>
                  ) : null}
                  {outcome?.relatedTicketId ? (
                    <Button
                      variant="outlined"
                      size="small"
                      endIcon={<OpenInNewIcon />}
                      onClick={() =>
                        history.push(`/admin/support-tickets/${outcome.relatedTicketId}`)
                      }
                    >
                      Open Support Ticket
                    </Button>
                  ) : null}
                  {!outcome?.reversible ? (
                    <Typography className={classes.mutedText}>
                      This outcome is already reversed.
                    </Typography>
                  ) : null}
                </div>

                {outcome?.reversible ? (
                  <>
                    <Divider style={{ margin: "16px 0" }} />
                    <div className={classes.reversalSection}>
                      <Typography className={classes.metadataLabel}>
                        Reversal Reason
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        variant="outlined"
                        placeholder="Explain why this leaver outcome should be reversed."
                        value={reversalReason}
                        onChange={(event) =>
                          setReversalReasons((currentReasons) => ({
                            ...currentReasons,
                            [historyId]: event.target.value,
                          }))
                        }
                      />
                      <div className={classes.actionRow}>
                        <Button
                          color="secondary"
                          variant="contained"
                          disabled={!reversalReason.trim() || isSubmitting}
                          onClick={() => handleReverse(outcome)}
                        >
                          {isSubmitting ? "Reversing..." : "Reverse Outcome"}
                        </Button>
                        <Typography className={classes.mutedText}>
                          Reversal triggers an immediate rebuild for this player.
                        </Typography>
                      </div>
                    </div>
                  </>
                ) : null}
              </Paper>
            );
          })}
        </div>
      ) : null}

      <Snackbar
        open={Boolean(snackbarMessage)}
        autoHideDuration={4000}
        onClose={() => setSnackbarMessage("")}
        message={snackbarMessage}
      />
    </div>
  );
}

ReputationOutcomesPage.propTypes = {
  catalog: PropTypes.shape({
    user: PropTypes.shape({
      userId: PropTypes.string,
    }),
  }),
};
